import {
  InternalAuditAiAuditItemDTO,
  InternalAuditAiAuditReportLinkDTO,
  InternalAuditAiAuditStatusDTO,
  InternalAuditConclusionSuggestionDTO,
  AiControlAuditCheckRowDTO,
  AiControlAuditFindingDTO,
  AiControlAuditReportDTO,
  AiControlDocumentAssessmentDTO,
  InternalAuditControlAuditResultDTO,
} from 'models/dto/internalAuditAiAuditDTO';
import {
  AiControlAuditCheckRow,
  AiControlAuditFinding,
  AiControlAuditReport,
  AiControlDocumentAssessment,
  InternalAuditAiAuditItem,
  InternalAuditAiAuditReportLink,
  InternalAuditAiAuditStatus,
  InternalAuditConclusionReason,
  InternalAuditConclusionSuggestion,
  InternalAuditControlAuditResult,
} from 'models/internalAuditAiAudit';
import { AuditOutcome } from 'models/internalAudit';

export function mapFromInternalAuditAiAuditStatus(dto: InternalAuditAiAuditStatusDTO): InternalAuditAiAuditStatus {
  const output = new InternalAuditAiAuditStatus();
  output.internalAuditId = dto.internalAuditId;
  output.runStatus = dto.runStatus;
  output.items = (dto.items ?? []).map(mapFromInternalAuditAiAuditItem);

  return output;
}

function mapFromInternalAuditAiAuditItem(dto: InternalAuditAiAuditItemDTO): InternalAuditAiAuditItem {
  const output = new InternalAuditAiAuditItem();
  output.entityType = dto.entityType;
  output.entityId = dto.entityId;
  output.status = dto.status;
  output.documentationStatus = dto.documentationStatus ?? undefined;
  output.evidenceStatus = dto.evidenceStatus ?? undefined;
  output.documentationAssessed = dto.documentationAssessed ?? undefined;
  output.evidenceAssessed = dto.evidenceAssessed ?? undefined;
  output.documentationSummary = dto.documentationSummary ?? '';
  output.evidenceSummary = dto.evidenceSummary ?? '';
  output.generatedOn = dto.generatedOn ? new Date(dto.generatedOn) : undefined;
  output.error = dto.error ?? '';

  return output;
}

export function mapFromInternalAuditControlAuditResult(
  dto: InternalAuditControlAuditResultDTO,
): InternalAuditControlAuditResult {
  const output = new InternalAuditControlAuditResult();
  output.controlId = dto.controlId;
  output.generatedOn = dto.generatedOn ? new Date(dto.generatedOn) : undefined;
  output.documentation = dto.documentation ? mapFromAiControlAuditReport(dto.documentation) : undefined;
  output.evidence = dto.evidence ? mapFromAiControlAuditReport(dto.evidence) : undefined;

  return output;
}

function mapFromAiControlAuditReport(dto: AiControlAuditReportDTO): AiControlAuditReport {
  const output = new AiControlAuditReport();
  output.status = dto.status;
  output.messageOnly = dto.messageOnly === true;
  output.controlCode = dto.controlCode ?? '';
  output.controlName = dto.controlName ?? '';
  output.managementSummary = dto.managementSummary ?? '';
  output.overallAssessment = dto.overallAssessment ?? '';
  // the API writes null for an absent rationale; the model wants undefined
  output.statusRationale = dto.statusRationale ?? undefined;
  output.documentAssessments = (dto.documentAssessments ?? []).map(mapFromAiControlDocumentAssessment);
  output.markdownReport = dto.markdownReport ?? '';
  output.findings = (dto.findings ?? []).map(mapFromAiControlAuditFinding);
  output.evidenceCheckRows = (dto.evidenceCheckRows ?? []).map(mapFromAiControlAuditCheckRow);
  output.limitations = dto.limitations ?? [];

  return output;
}

function mapFromAiControlDocumentAssessment(dto: AiControlDocumentAssessmentDTO): AiControlDocumentAssessment {
  const output = new AiControlDocumentAssessment();
  output.linkId = dto.linkId;
  output.name = dto.name ?? undefined;
  output.relevance = dto.relevance ?? '';
  output.rationale = dto.rationale ?? '';

  return output;
}

function mapFromAiControlAuditFinding(dto: AiControlAuditFindingDTO): AiControlAuditFinding {
  const output = new AiControlAuditFinding();
  output.severity = dto.severity;
  output.title = dto.title ?? '';
  output.observation = dto.observation ?? '';
  output.risk = dto.risk ?? '';
  output.recommendation = dto.recommendation ?? '';
  output.reference = dto.reference ?? '';

  return output;
}

function mapFromAiControlAuditCheckRow(dto: AiControlAuditCheckRowDTO): AiControlAuditCheckRow {
  const output = new AiControlAuditCheckRow();
  output.checkKey = dto.checkKey ?? '';
  output.requirement = dto.requirement ?? '';
  output.successCriteria = dto.successCriteria ?? '';
  output.mode = dto.mode ?? '';
  output.evidenceStatus = dto.evidenceStatus ?? '';
  output.taskId = dto.taskId;
  output.bound = dto.bound === true;

  return output;
}

export function mapFromInternalAuditAiAuditReportLink(
  dto: InternalAuditAiAuditReportLinkDTO,
): InternalAuditAiAuditReportLink {
  const output = new InternalAuditAiAuditReportLink();
  output.linkId = dto.linkId;
  output.linkName = dto.linkName ?? '';
  output.linkURL = dto.linkURL ?? '';

  return output;
}

export function mapFromInternalAuditConclusionSuggestion(
  dto: InternalAuditConclusionSuggestionDTO,
): InternalAuditConclusionSuggestion {
  const output = new InternalAuditConclusionSuggestion();
  output.outcome = (dto.outcome as AuditOutcome) ?? AuditOutcome.NotAssessed;
  output.conclusion = dto.conclusion ?? '';
  output.reason = (dto.reason as InternalAuditConclusionReason) ?? InternalAuditConclusionReason.None;

  return output;
}
